import { put, call, takeLatest, select } from "redux-saga/effects";
import get from "lodash/get";
import userActions, { userActionTypes } from "action-creators/user";
import { history } from "store";
import AuthActions, { AuthTypes, AuthSelectors } from "reducers/AuthRedux";
import Requester from "common/network/http/Request";
import endpoints from "configs/endpoints";
import firebase from "./firebase";
import { removeCookies, setCookie } from "cookies-next";
import { COMMONGROUND_SESSION } from "configs/constants";
import cookiesUtils from "../common/cookies.utils";

function* handleUserLogout(action) {
  const { redirectURL: url } = action;
  const isImpersonating = yield select(AuthSelectors.isImpersonating);

  if (isImpersonating) {
    yield put(AuthActions.toggleImpersonateUser());
  } else {
    const redirect = history.location.pathname;
    const redirectURL = redirect && redirect !== "/" && redirect !== "/login"
      ? `${history.location.pathname}${history.location.search}`
      : null;

    yield put(userActions.onLoginSuccess({}));
    yield put(AuthActions.cleanUpSession());
    removeCookies(COMMONGROUND_SESSION.token);
    removeCookies(COMMONGROUND_SESSION.accessToken);
    firebase.auth.signOut();

    let newURL = "/login";

    if (redirectURL) {
      newURL += `?redirect=${encodeURIComponent(redirectURL)}`;
    }


    yield call(history.push, url || newURL);
  }
}

function* handleUserLogin(action) {
  const { user, token, redirect } = action;

  try {
    const result = yield call(Requester.post.bind(Requester), endpoints.user.login, { token });
    const error = get(result, "data.error");

    if (!error) {
      const currentUser = result.data.user;

      yield put(userActions.onLoginSuccess({
        ...result.data,
        refreshToken: user.refreshToken,
      }));

      if (user.providerData) {
        yield put(AuthActions.setCurrentUser(user.providerData.pop()));
      } else {
         yield put(AuthActions.setCurrentUser(currentUser));
      }
      const url = redirect ? decodeURIComponent(redirect) : "/";

      if (currentUser.hasBasicProfile && currentUser.hasEmailVerified) {
        if (url.includes("http")) {
          window.location.href = url;
        } else {
          yield call(history.push, url);
        }
      } else {
        yield call(history.push, `/information?redirect=${url}`);
      }
    }
  } catch (err) {
    firebase.auth.signOut();
  }
}

function* handleRestoreSession() {
  const tokens = cookiesUtils.getTokens();
  if (tokens) {
    try {
      const result = yield call(Requester.post.bind(Requester), endpoints.user.login, { token: tokens.accessToken });
      const error = get(result, "data.error");

      if (!error) {
        const currentUser = result.data.user;

        yield put(userActions.onLoginSuccess({
          ...result.data,
          refreshToken: tokens.refreshToken,
        }));

        yield put(AuthActions.setCurrentUser(currentUser));

        if (tokens.impersonationTarget) {
          yield put(AuthActions.toggleImpersonateUser(tokens.impersonationTarget, false));
        }
      }
    } catch (err) {
      firebase.auth.signOut();
    }
  } else {
    yield put(userActions.logout());
  }
}

function* handleToggleImpersonation(action) {
  const { userId } = action;

  if (userId) {
    const res = yield call(Requester.get.bind(Requester), endpoints.user.getUser(userId));

    if (res.status === 200) {
      yield put(userActions.setUser(res.data));
      if (action.redirect) yield call(history.push, "/");
      yield put(AuthActions.toggleImpersonateUserSuccess(userId));
      setCookie(COMMONGROUND_SESSION.impersonationTarget, userId);
    } else {
      yield put(AuthActions.toggleImpersonateUserSuccess(null));
      setCookie(COMMONGROUND_SESSION.impersonationTarget, "");
    }
  } else {
    yield put(AuthActions.toggleImpersonateUserSuccess(null));
    const res = yield call(Requester.get.bind(Requester), endpoints.user.getMe);
    setCookie(COMMONGROUND_SESSION.impersonationTarget, "");

    yield put(userActions.setUser(res.data));
    yield call(history.push, "/admin/users");
  }

  window.scrollTo(0, 0);
}

function* handleSignUp(action) {
  const { user, redirect } = action;
  try {
    const result = yield call(Requester.post.bind(Requester), endpoints.user.createCredentials, user);
    if (result.status === 201) {
      yield put(AuthActions.authenticate(user, result.data.token, redirect));
    } else if (result.status === 409) {
      yield call(history.push, `/login?email=${encodeURIComponent(user.email)}&redirect=${redirect}`);
      yield put(AuthActions.signUpError());
    } else {
      yield put(AuthActions.signUpError());
    }
  } catch (error) {
    yield put(AuthActions.signUpError());
  }
}

export default function* () {
  return [
    yield takeLatest(userActionTypes.LOGOUT_FROM_APPLICATION, handleUserLogout),
    yield takeLatest(AuthTypes.AUTHENTICATE, handleUserLogin),
    yield takeLatest(AuthTypes.TOGGLE_IMPERSONATE_USER, handleToggleImpersonation),
    yield takeLatest(AuthTypes.SIGN_UP, handleSignUp),
    yield takeLatest(AuthTypes.RESTORE_SESSION, handleRestoreSession),
  ];
}
